'use client';

import { cn } from '@/lib/utils';

/* ---------- Vertical Timeline ---------- */

export interface VerticalStage {
  name: string;
  description?: string;
  emotion?: string;
  steps?: string[];
  touchpoints?: string[];
  actions?: string[];
}

interface VerticalStageFlowProps {
  stages: VerticalStage[];
  getEmotionColor?: (emotion: string) => string;
}

const defaultEmotionColors: Record<string, string> = {
  anxious: 'bg-amber-100 text-amber-700',
  frustrated: 'bg-red-100 text-red-700',
  confused: 'bg-orange-100 text-orange-700',
  skeptical: 'bg-yellow-100 text-yellow-700',
  neutral: 'bg-gray-100 text-gray-700',
  curious: 'bg-blue-100 text-blue-700',
  hopeful: 'bg-cyan-100 text-cyan-700',
  confident: 'bg-green-100 text-green-700',
  satisfied: 'bg-emerald-100 text-emerald-700',
  delighted: 'bg-teal-100 text-teal-700',
};

function resolveEmotionColor(emotion?: string): string {
  if (!emotion) return 'bg-gray-100 text-gray-700';
  const key = emotion.toLowerCase().replace(/[^a-z]/g, '');
  for (const [k, v] of Object.entries(defaultEmotionColors)) {
    if (key.includes(k)) return v;
  }
  return 'bg-purple-100 text-purple-700';
}

/** Vertical timeline with numbered stage cards (used by Journeys) */
export function VerticalStageFlow({ stages, getEmotionColor }: VerticalStageFlowProps) {
  const colorFn = getEmotionColor || resolveEmotionColor;

  return (
    <div className="relative">
      <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-brand-primary via-purple-400 to-brand-primary/30" />
      <div className="space-y-5">
        {stages.map((stage, index) => (
          <div key={index} className="relative pl-12">
            <div className="absolute left-2 top-2 w-7 h-7 rounded-full bg-brand-primary text-white text-xs flex items-center justify-center font-bold shadow-md border-4 border-white">
              {index + 1}
            </div>
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900 text-base">{stage.name || `Stage ${index + 1}`}</h4>
                {stage.emotion && (
                  <span className={cn('px-3 py-1 rounded-full text-xs font-medium', colorFn(stage.emotion))}>
                    {stage.emotion}
                  </span>
                )}
              </div>
              {stage.description && (
                <p className="text-gray-600 text-sm mb-3 leading-relaxed">{stage.description}</p>
              )}
              <StepList steps={stage.steps} />
              <TouchpointPills items={stage.touchpoints} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Horizontal Flow ---------- */

export interface HorizontalStage {
  name: string;
  description?: string;
  actions?: string[];
}

interface HorizontalStageFlowProps {
  stages: HorizontalStage[];
}

/** Horizontal flow with numbered stage cards and arrows (used by Service Maps) */
export function HorizontalStageFlow({ stages }: HorizontalStageFlowProps) {
  return (
    <div className="flex flex-wrap items-start gap-3">
      {stages.map((stage, index) => (
        <div key={index} className="flex items-start">
          <div className="px-4 py-3 bg-gradient-to-br from-brand-primary/5 to-purple-50 border border-brand-primary/20 rounded-xl min-w-[160px]">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-brand-primary text-white text-xs flex items-center justify-center font-semibold">{index + 1}</span>
              <span className="font-semibold text-gray-900 text-sm">{stage.name || `Stage ${index + 1}`}</span>
            </div>
            {stage.description && (
              <p className="text-xs text-gray-600 mt-1 ml-8">{stage.description}</p>
            )}
            {Array.isArray(stage.actions) && stage.actions.length > 0 && (
              <ul className="mt-2 ml-8 space-y-1">
                {stage.actions.map((action, ai) => (
                  <li key={ai} className="text-xs text-gray-500 flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 shrink-0" />
                    {action}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {index < stages.length - 1 && (
            <span className="mx-2 mt-4 text-brand-primary/50 text-lg">→</span>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Ordered Step List ---------- */

interface OrderedStepListProps {
  steps: { step?: number; action?: string; description?: string }[] | string[];
}

/** Numbered ordered step list (used by Use Case main flow) */
export function OrderedStepList({ steps }: OrderedStepListProps) {
  if (!steps || steps.length === 0) return null;
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => {
        const text = typeof s === 'string' ? s : (s.action || s.description || String(s));
        return (
          <li key={i} className="flex items-start gap-3 text-sm">
            <span className="w-6 h-6 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">
              {typeof s === 'object' && s.step ? s.step : i + 1}
            </span>
            <span className="text-gray-700 leading-relaxed">{text}</span>
          </li>
        );
      })}
    </ol>
  );
}

/* ---------- Sub-components ---------- */

function StepList({ steps }: { steps?: string[] }) {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  return (
    <div className="space-y-1.5 mt-3">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <span className="w-5 h-5 rounded bg-white border border-gray-200 flex items-center justify-center text-xs text-gray-500 font-medium shrink-0 mt-0.5">
            {i + 1}
          </span>
          <span className="text-gray-700">{step}</span>
        </div>
      ))}
    </div>
  );
}

function TouchpointPills({ items }: { items?: string[] }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200">
      {items.map((tp, i) => (
        <span key={i} className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs">
          {tp}
        </span>
      ))}
    </div>
  );
}
